<script lang="ts" setup>import { computed as _computed } from 'vue';

import {
  ISOPRO,
  ISOPROProductHeightOffsetVariantType,
  ISOPROProductSMVariantType,
  ISOPROProductSMType,
  ISOPROProductMType,
  ISOPROProductDType,
} from "~/models";
import { AdaptedISOPROType, DataTableModel, VariantGroupKey, sortVariantKeys } from "./models";
import ProductDataTableFixedPart from "./ProductDataTableFixedPart.vue";
import { computed, ref, onBeforeMount } from 'vue';
import { useProductsStore } from "~/store";
import EditHeatFlowModal from "~/components/EditHeatFlowModal.vue";
import { UpdateHeatFlowDto } from "~/api/webservice";

import { useAuthStore } from "~/store/auth.store";

interface Props {
  model: DataTableModel<ISOPRO>;
  selectedType: AdaptedISOPROType;
  cellWidth?: number;
}

type HeaderKeys = Record<string | number, Partial<Record<VariantGroupKey, Set<string>>>>;

const authStore = useAuthStore();

const draftHeatFlow = ref<number | null>(null);
const isEditModalOpen = ref(false);

const selectedProduct = computed(() => store.selectedProduct);

const props = withDefaults(defineProps<Props>(), {
  cellWidth: 50,
});
const store = useProductsStore();

const adaptedVariantTypes = computed(() => store.adaptedVariantTypes(props.selectedType));

const headerKeys: HeaderKeys = _computed(() => {
  // We assume that with the object structure the keys are in the right order
  const result: HeaderKeys = {};
  Object.values(props.model).forEach((data) => {
    if (data) {
      Object.values(data).forEach((data) => {
        Object.entries(data).filter(([lbc, variantGroupCollection]) => {
          const show = Object.keys(variantGroupCollection).filter((variant) => {
            const assert = adaptedVariantTypes.value.includes(variant as VariantGroupKey);
            return assert;
          });
          return show.length;
        }).forEach(([lbc, variantGroupCollection]) => {
          if (!result[lbc] && Object.values(variantGroupCollection).length) result[lbc] =
            Object.keys(variantGroupCollection).reduce((r, variant) => {
              if (adaptedVariantTypes.value.includes(variant as VariantGroupKey))
                r[variant as VariantGroupKey] = new Set<string>();
              return r;
            }, {} as Record<VariantGroupKey, Set<string>>);

          Object.entries(variantGroupCollection).forEach(([variant, productArray]) => {
            if (adaptedVariantTypes.value.includes(variant as VariantGroupKey)) {
              if (!result[lbc][variant as VariantGroupKey]) {
                result[lbc][variant as VariantGroupKey] = new Set<string>();
              }
              productArray.forEach((p) => {
                const hasShearForceLbc = p instanceof ISOPROProductMType
                  || p instanceof ISOPROProductDType
                  || p instanceof ISOPROProductHeightOffsetVariantType
                  || p instanceof ISOPROProductSMVariantType
                  || p instanceof ISOPROProductSMType;

                const isVariantType = p instanceof ISOPROProductHeightOffsetVariantType
                  || p instanceof ISOPROProductSMVariantType;

                if (hasShearForceLbc) {
                  if (isVariantType) {
                    result[lbc][p.variant]?.add(p.shearForceLoadBearingCapacityCode);
                  } else
                    result[lbc]['none']?.add(p.shearForceLoadBearingCapacityCode);
                }
              });
            }
          });
        });
      });
    }
  });
  return result;
});

const totalColumns = _computed(() =>
  Object.values(headerKeys.value).reduce((t, heightOffsetTypeGroup) => {
    const setEntities = Object.values(heightOffsetTypeGroup).reduce((r, set) => r + Math.max(set.size, 1), 0);
    return t + setEntities;
  }, 0)
);

const hasShearForceRow = _computed(() =>
  Object.values(headerKeys.value).some((variantGroup) =>
    Object.values(variantGroup).some((set) => set.size)));

const hasVariantRow = computed(() => {
  return Object.values(headerKeys.value).some((variantGroup) =>
    Object.entries(variantGroup).filter(([variant, set]) => variant !== 'none')
      .some(([_, set]) => set.size));
});

function openEditModal (product: ISOPRO) {
  store.selectedProduct = product;
  draftHeatFlow.value = product.heatFlow ?? null;
  isEditModalOpen.value = true;
}

async function onSubmitHeatFlow (): Promise<void> {
  if (!selectedProduct.value) return;
  if (!draftHeatFlow.value) return;

  const payload: UpdateHeatFlowDto = {
    rowKey: selectedProduct.value.rowKey,
    heatFlow: draftHeatFlow.value,
    productType: selectedProduct.value.type,
  };

  if ('variant' in selectedProduct.value) {
    payload.variant = selectedProduct.value.variant;
  }
  await store.updateHeatFlow(payload);

  Object.assign(selectedProduct.value, { heatFlow: draftHeatFlow.value });

  isEditModalOpen.value = false;
}

onBeforeMount(async () => {
  await authStore.boot();
  if (!authStore.isAuthenticated) {
    authStore.login();
  }
});

</script>

<template>
  <div class="data-table__container">
    <ProductDataTableFixedPart
      :model="model"
      :hasShearForceRow="hasShearForceRow"
      :hasVariantRow="hasVariantRow"
    />

    <div class="data-table__wrapper">
      <table class="data-table">
        <thead>
          <tr>
            <th
              class="data-table__group"
              :colspan="totalColumns"
            >
              Capacity
            </th>
          </tr>
          <tr>
            <th
              v-for="(variantGroup, loadBearingCapacity) in headerKeys"
              :key="loadBearingCapacity"
              class="data-table__group"
              :colspan="Object.values(Object.values(variantGroup)).reduce((t, set) => t + Math.max(set.size, 1), 0)"
            >
              {{ loadBearingCapacity }}
            </th>
          </tr>
          <tr v-if="hasVariantRow">
            <template
              v-for="(variantGroup, lbc) in headerKeys"
              :key="lbc"
            >
              <th
                v-for="(set, variantType) in sortVariantKeys(variantGroup)"
                :key="variantType"
                class="data-table__group"
                :colspan="set?.size"
              >
                {{ variantType }}
              </th>
            </template>
          </tr>
          <tr v-if="hasShearForceRow">
            <template
              v-for="(variantGroup, loadBearingCapacity) in headerKeys"
              :key="loadBearingCapacity"
            >
              <template
                v-for="(set, variantType) in variantGroup"
                :key="variantType"
              >
                <th
                  v-for="(shearForce, i) in set"
                  class="data-table__group"
                  :key="i"
                >
                  {{ shearForce }}
                </th>
              </template>
            </template>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(heightCollection, height) in model"
            :key="height"
          >
            <tr
              v-for="(fireProtectionCollection, fireProtection) in heightCollection"
              :key="fireProtection"
            >
              <template
                v-for="(
loadBearingCapacityCollection, loadBearingCapacity
                ) in fireProtectionCollection"
                :key="loadBearingCapacity"
              >
                <template
                  v-for="(productArray, variantType) in loadBearingCapacityCollection"
                  :key="variantType"
                >
                  <template v-if="adaptedVariantTypes.includes(variantType)">
                    <td
                      v-for="product in productArray"
                      :key="product.rowKey"
                      :title="product.rowKey"
                      :style="[{ 'min-width': cellWidth + 'px' }, product.heatFlow ? { 'height': 'inherit' } : { 'height': '31px' }]"
                      @click="openEditModal(product)"
                    >
                      {{ product.heatFlow?.toFixed(3) || "" }}
                    </td>
                  </template>
                </template>
              </template>
            </tr>
          </template>
          <EditHeatFlowModal
            v-model="isEditModalOpen"
            v-model:draftHeatFlow="draftHeatFlow"
            :rowKey="selectedProduct?.rowKey ?? null"
            :heatFlow="selectedProduct?.heatFlow ?? undefined"
            @submit="onSubmitHeatFlow"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss">
.data-table {
  font-size: 14px;
  border-spacing: 2px;

  &.fixed {
    margin-bottom: 18px;
  }

  &__container {
    display: grid;
    grid-template-columns: max-content 1fr;
  }

  &__wrapper {
    width: 100%;
    overflow: auto;
  }

  &__group {
    text-align: center;
  }

  td,
  th {
    height: 30px;
    padding: 5px;
  }

  tbody {
    td {
      text-align: center;
      background-color: lightgray;
      font-size: 12px;
      font-family: monospace;
      cursor: pointer;
    }
  }

  th {
    background-color: var(--bds-brand-primary-color);
    color: white;
  }
}

.cell-error {
  color: #e53935;
  font-size: 0.75rem;
}
</style>
